import type { HomeCopy } from '../types/home';

export const homeDe: HomeCopy = {
  hero: {
    eyebrow: 'Für Restaurants, Cafés & Bars',
    title: {
      line1: 'Ihre Speisekarte,',
      pre: 'auf einem ',
      highlight: 'QR-Code',
      post: ',',
      line3: 'in Minuten.',
    },
    subtitle: {
      intro:
        'Gäste scannen eine kleine Karte auf dem Tisch, und Ihre Speisekarte öffnet sich auf ihrem Handy. ',
      easyToUse: 'Einfach zu bedienen',
      comma: ',',
      safeForGuests: 'sicher für Gäste',
      and: 'und',
      kindToNature: 'schonend für die Natur',
      outro: '. Keine App, kein Nachdrucken, kein Aufwand.',
    },
    primaryCta: 'Kostenlos starten - keine Kreditkarte nötig',
    secondaryCta: 'So funktioniert es',
    checklist: [
      'Startklar in ~15 Minuten',
      'Funktioniert auf jedem Handy',
      'Kostenloser Tarif inklusive',
    ],
  },
  trustStrip: {
    ariaLabel: 'Warum Restaurants sich für SafestMenu entscheiden',
    words: [
      'Einfach zu bedienen',
      'Vertraut von Gästen',
      'Sicher & hygienisch',
      'Schonend für die Natur',
      'Keine App nötig',
      'Kostenlos starten',
    ],
  },
  why: {
    eyebrow: 'Warum auf Papier verzichten',
    heading: 'Papier-Speisekarten kosten Geld, verbreiten Keime und landen im Müll.',
    cards: [
      {
        alt: 'Ein makelloser Restauranttisch mit einem kleinen QR-Code-Ständer anstelle von Papierspeisekarten',
        title: 'Eine Speisekarte, die sonst niemand berührt hat',
        text: {
          pre: 'Eine Papierspeisekarte geht durch Hunderte Hände und wird fast nie gewaschen. Mit einem QR-Code stöbert jeder Gast auf dem eigenen Handy - die',
          highlight: 'hygienischste Speisekarte',
          post: 'überhaupt.',
        },
      },
      {
        alt: 'Ein Restaurantbesitzer aktualisiert seine Speisekarte vom Handy aus an der Ausgabe in der Küche',
        title: 'Einen Preis in 30 Sekunden ändern',
        text: {
          pre: 'Neues Gericht? Lieferant hat die Preise erhöht? Ändern Sie es auf dem Handy, und jeder Tisch sieht die Aktualisierung sofort. Es ist wirklich',
          highlight: 'einfach zu bedienen',
          post: ' - wenn Sie eine SMS schreiben können, können Sie SafestMenu bedienen.',
        },
      },
      {
        alt: 'Ein Stapel abgenutzter Papierspeisekarten neben einem einzigen wiederverwendbaren QR-Code-Ständer und einer grünen Pflanze',
        title: 'Einmal drucken, nie wieder',
        text: {
          pre: 'Jede Saison neu gedruckter, laminierter Speisekarten bedeutet Geld und Papier im Müll. Eine kleine Karte pro Tisch ist',
          highlight: 'schonend für die Natur',
          post: '- und für Ihr Budget.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'So funktioniert es',
    heading: 'Drei Schritte. Keine technischen Kenntnisse nötig.',
    steps: [
      {
        title: 'Speisekarte eintippen',
        text: 'Fügen Sie Ihre Gerichte und Preise hinzu. Fehlen Ihnen die richtigen Worte? Unsere KI schreibt appetitliche Beschreibungen und erstellt sogar Fotos für Gerichte, von denen Sie noch keine haben.',
      },
      {
        title: 'QR-Code drucken',
        text: 'Laden Sie druckfertige Tischkarten in allen gängigen Größen herunter. Stellen Sie sie auf Tische, ins Schaufenster, auf die Theke - überall dort, wo Ihre Gäste Platz nehmen.',
      },
      {
        title: 'Gäste scannen und bestellen',
        text: 'Keine App zum Installieren. Die Speisekarte öffnet sich im Handy-Browser - mit Fotos, Allergen-Infos und in ihrer eigenen Sprache.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'Neu - in Minuten importiert',
    heading: 'Haben Sie bereits eine Speisekarte? Starten Sie damit.',
    paragraph:
      'Sie müssen nicht alles abtippen. Laden Sie ein PDF oder ein paar Fotos Ihrer gedruckten Speisekarte hoch, und SafestMenu liest sie ein - Gerichte, Preise, Kategorien - und übergibt Ihnen einen sauberen Entwurf zum Prüfen und Veröffentlichen. Eine typische Speisekarte passt in das kostenlose Guthaben, das Sie bei der Anmeldung erhalten.',
    steps: [
      {
        title: 'Speisekarte hochladen',
        text: 'Legen Sie ein PDF oder bis zu 40 Fotos ab. Sie sehen die genauen Kosten vorab - 2 Guthaben pro Seite - bevor etwas berechnet wird.',
      },
      {
        title: 'KI liest jede Seite',
        text: 'Aus jeder Seite werden bearbeitbare Gerichte, Preise und Kategorien, während Speisekartensprache und Währung automatisch erkannt werden.',
      },
      {
        title: 'Prüfen und veröffentlichen',
        text: 'Sie landen auf einem übersichtlichen Prüfbildschirm, passen an, was noch fehlt, und erstellen dann eine neue Speisekarte oder fügen die Seiten einer bestehenden hinzu.',
      },
    ],
  },
  productTour: {
    eyebrow: 'In Aktion erleben',
    heading: 'Sehen Sie, wie eine Speisekarte entsteht.',
    paragraph:
      'Von der leeren Seite bis live auf dem Tisch. Das ist der echte Editor - fügen Sie ein Gericht hinzu, lassen Sie die KI die Beschreibung schreiben, ordnen Sie alles per Drag-and-Drop und veröffentlichen Sie. Kein Demo-Call nötig.',
  },
  aiStudio: {
    eyebrow: 'Eingebaute KI',
    heading: {
      pre: 'Kein Fotograf?',
      highlight: 'Kein Problem.',
    },
    paragraph:
      'Machen Sie ein schnelles Foto - oder auch gar keins - und lassen Sie SafestMenu schöne, einheitliche Fotos Ihrer Gerichte erstellen. Die appetitlichen Beschreibungen schreibt die KI gleich mit. Sie behalten die Kontrolle: Wählen Sie die Aufnahme, die Ihnen gefällt, behalten Sie sie, fertig.',
    pills: [
      'KI-Beschreibungen · 1 Guthaben',
      'KI-Gerichtfotos · 5 Guthaben',
      'Speisekartenübersetzungen · je 1 Guthaben',
      'Speisekarten-Scan · 2 Guthaben/Seite',
      'KI-Allergenvorschläge · 1 Guthaben',
      'Guthaben verfällt nie',
    ],
  },
  features: {
    eyebrow: 'Alles inklusive',
    heading: 'Gemacht für Restaurants, nicht für IT-Abteilungen.',
    sidebarCaption:
      'Jeder Blick auf ein Gericht wird automatisch gezählt - so sehen Sie, worauf Ihre Gäste Appetit haben, und stellen Ihre Bestseller in den Mittelpunkt.',
    items: [
      {
        title: 'Preise aktualisieren sich sofort',
        text: 'Bearbeiten Sie alles vom Handy aus. Jeder QR-Code an jedem Tisch zeigt immer die aktuelle Speisekarte.',
      },
      {
        title: 'KI-Fotos & -Beschreibungen',
        text: 'Kein Fotograf? Die eingebaute KI schreibt appetitliche Beschreibungen und erstellt Fotos Ihrer Gerichte für Sie.',
      },
      {
        title: 'Alle 14 EU-Allergene',
        text: 'Markieren Sie Allergene einmal pro Gericht. Gäste sehen klare Kennzeichnungen direkt am Tisch - kein Rätselraten mehr, keine riskanten Antworten mehr.',
      },
      {
        title: 'Sechs Sprachen inklusive',
        text: 'Auch Touristen sind Gäste. Ihre Speisekarte spricht Englisch, Deutsch, Spanisch, Französisch, Italienisch und Rumänisch.',
      },
      {
        title: 'Speisekarte mit einem Klick übersetzen',
        text: 'Die KI übersetzt jedes Gericht im eigenen Tonfall Ihrer Speisekarte, für je 1 Guthaben. Bearbeiten Sie ein Gericht später, wird nur dieses Gericht neu übersetzt - so bleibt alles für ein paar Cent aktuell.',
      },
      {
        title: 'Speisekarten, die nach Zeitplan wechseln',
        text: 'Führen Sie getrennte Karten für Frühstück, Mittag- und Abendessen. Legen Sie die Zeiten einmal fest - derselbe QR-Code zeigt immer die Speisekarte, die gerade serviert wird.',
      },
      {
        title: 'Jede Speisekarte duplizieren',
        text: 'Kopieren Sie eine ganze Speisekarte samt Gerichten und Fotos, passen Sie an, was sich geändert hat, und veröffentlichen Sie, sobald alles passt. Praktisch für saisonale Karten und weitere Standorte.',
      },
      {
        title: 'Sehen Sie, worauf Gäste Appetit haben',
        text: 'Einfache Statistiken zeigen, welche Gerichte am häufigsten angesehen werden - so wissen Sie, was auf die Tageskarte gehört.',
      },
      {
        title: 'Ihr Look, Ihre Marke',
        text: 'Wählen Sie Ihre Farben und Schriften, fügen Sie Ihr Logo und Fotos Ihrer Gerichte hinzu. Ihre Speisekarte sieht aus wie Ihr Restaurant, nicht wie unseres.',
      },
      {
        title: 'Starten Sie mit Ihrer gedruckten Speisekarte',
        text: 'Haben Sie bereits eine gedruckte Speisekarte? Laden Sie ein PDF oder ein paar Fotos hoch, und die App liest daraus bearbeitbare Gerichte und Preise ein - eine typische Speisekarte passt in Ihr kostenloses Startguthaben.',
      },
      {
        title: 'KI schlägt die Allergene vor',
        text: 'Bearbeiten Sie ein Gericht? Lassen Sie die KI Name und Beschreibung lesen und die 14 EU-Allergene für 1 Guthaben vorschlagen. Sie prüfen und bestätigen - ein Vorsprung, nie das letzte Wort.',
      },
      {
        title: 'Gäste filtern die Speisekarte selbst',
        text: 'Am Tisch öffnen Gäste einen Filter, wählen aus, was sie meiden möchten oder welche Ernährungsweise sie brauchen, und ungeeignete Gerichte treten zur Seite. Eingebaut, immer aktiv, nichts einzurichten.',
      },
      {
        title: 'Machen Sie aus Gästen Google-Bewertungen',
        text: 'Fügen Sie Ihren Google-Bewertungslink hinzu, und Ihre Speisekarte endet mit einem freundlichen Anstoß - zufriedene Gäste tippen durch und hinterlassen direkt am Tisch eine Bewertung, solange das Essen noch frisch in Erinnerung ist.',
      },
    ],
  },
  guests: {
    eyebrow: 'Gäste lieben es',
    heading: {
      pre: 'Eine Speisekarte, der Ihre Gäste wirklich',
      highlight: 'vertrauen',
      post: ' können.',
    },
    paragraph:
      'Klare Fotos, ehrliche Preise, Allergene bei jedem Gericht markiert und die gesamte Speisekarte in ihrer eigenen Sprache. Gäste bestellen mit Zuversicht - und selbstbewusste Gäste bestellen mehr.',
    checklist: [
      'Öffnet sich im Browser - nichts zu installieren',
      'Allergenkennzeichnung bei jedem einzelnen Gericht',
      'Ein Fingertipp übersetzt die gesamte Speisekarte in ihre Sprache',
    ],
  },
  pricing: {
    eyebrow: 'Preise',
    heading: 'Kostenlos starten. Kostenlos bleiben, wenn es reicht.',
    paragraph:
      'Ihre erste Speisekarte mit fünf Gerichten ist kostenlos - ohne Kreditkarte. Wenn Ihre Speisekarte wächst, wechseln Sie zu unbegrenzten Gerichten und mehr KI-Guthaben für Beschreibungen, Fotos und Übersetzungen. Alle sechs Sprachen sind in jedem Tarif enthalten. Jederzeit kündbar.',
    price: '€0',
    priceCaption: 'für den Einstieg',
    cta: 'Kostenlose Speisekarte erstellen',
    disclaimer: 'Keine Kreditkarte. Kein Zeitlimit.',
  },
  faq: {
    eyebrow: 'Häufig gestellte Fragen',
    heading: 'Was Restaurantbetreiber über SafestMenu wissen möchten.',
    items: [
      {
        question: 'Was ist SafestMenu und wie lautet die offizielle Website?',
        answer:
          'SafestMenu ist eine Plattform für QR-Code-Speisekarten für Restaurants, Cafés und Bars. Die offizielle Website ist safestmenu.com; Restaurantbetreiber verwalten ihre Speisekarten auf app.safestmenu.com.',
      },
      {
        question: 'Wie funktioniert eine QR-Code-Speisekarte von SafestMenu?',
        answer:
          'Sie erstellen und veröffentlichen eine digitale Speisekarte und drucken anschließend den QR-Code für Ihre Tische. Gäste scannen ihn mit der Handykamera und die aktuelle Speisekarte öffnet sich im Browser.',
      },
      {
        question: 'Müssen Gäste eine App installieren?',
        answer:
          'Nein. Gäste öffnen Ihre Speisekarte im Browser ihres Handys. Es ist weder ein Download noch ein Benutzerkonto nötig.',
      },
      {
        question: 'Kann ich Gerichte und Preise nach der Veröffentlichung ändern?',
        answer:
          'Ja. Bearbeiten Sie Ihre Speisekarte am Handy oder Computer und veröffentlichen Sie die Aktualisierung. Derselbe gedruckte QR-Code zeigt danach die neueste Version.',
      },
      {
        question: 'Kann ich Allergene kennzeichnen und die Speisekarte mehrsprachig anbieten?',
        answer:
          'Ja. Sie können Gerichte mit den 14 EU-Allergenen kennzeichnen, Gäste Gerichte filtern lassen und Ihre Speisekarte auf Englisch, Deutsch, Spanisch, Französisch, Italienisch und Rumänisch anbieten.',
      },
      {
        question: 'Kann ich kostenlos mit einer QR-Code-Speisekarte starten?',
        answer:
          'Ja. Der kostenlose Tarif enthält eine Speisekarte mit bis zu fünf Gerichten, erfordert keine Kreditkarte und hat kein Zeitlimit.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Probieren Sie es jetzt aus -',
      highlight: 'Scannen Sie diesen Code.',
    },
    paragraph:
      'Genau das werden Ihre Gäste am Tisch tun. Wenn Ihnen gefällt, was Sie sehen, ist Ihre eigene Speisekarte etwa fünfzehn Minuten entfernt.',
    cta: 'Starten Sie noch heute Ihre Speisekarte',
  },
  jsonLd: {
    appDescription:
      'QR-Code-Speisekarten für Restaurants, Cafés und Bars. Erstellen Sie in wenigen Minuten eine digitale Speisekarte oder importieren Sie Ihre bestehende Speisekarte aus einem PDF oder Fotos, aktualisieren Sie Preise sofort, schalten Sie Speisekarten nach Zeitplan um, markieren Sie Gerichte als nicht verfügbar, mit Allergenkennzeichnung, einem Allergenfilter für Gäste und Speisekartenübersetzung in sechs Sprachen.',
  },
};
